import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Globe, HeartPulse, MessageSquare } from "lucide-react";

const OurExpertise = () => {
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState("BPO");

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (titleRef.current) observer.observe(titleRef.current);
    if (descriptionRef.current) observer.observe(descriptionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const solutions = {
    BPO: [
      { title: "Inbound Call Management" },
      { title: "Outbound Sales and Support" },
      { title: "Email and Chat Support Services" },
      { title: "SMS Campaign Management" },
      { title: "Data Entry and Processing" },
      { title: "Customer Relationship Management (CRM)" },
    ],
    "Health Care": [
      { title: "Medical Billing and Coding" },
      { title: "Medical Transcription Services" },
      { title: "Insurance Claims Processing" },
      { title: "Healthcare Data Management" },
      { title: "Patient Scheduling and Follow-up Services" },
      { title: "Revenue Cycle Management" },
    ],
    "Digital Marketing": [
      { title: "Custom Web Development" },
      { title: "UI/UX Design and Consultation" },
      { title: "WordPress Development" },
      { title: "Search Engine Optimization (SEO)" },
      { title: "Social Media Marketing and Management" },
      { title: "Pay-Per-Click (PPC) Advertising" },
      { title: "Content Writing and Copywriting" },
      { title: "Graphic Design and Branding Solutions" },
    ],
  };

  return (
    <div className="text-gray-800" style={{ marginTop: "80px" }}>
      {/* Hero Section */}
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">Our Expertise </span>
                <span className="text-blue-600">Solutions</span>
              </h1>
              <p className="mt-4 font-bold">
                Empowering Your Business with Tailored Solutions
              </p>
              <p className="text-white mt-4 mb-8">
                At Right On Time Services, we are committed to building a better
                business world through comprehensive, customized solutions
                designed to optimize operations and drive growth. Our expertise
                spans across multiple industries, offering a blend of essential
                and advanced services to meet the evolving needs of businesses
                of all sizes.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section
          id="business-process-solutions"
          className="mt-4 py-24 bg-gradient-to-r from-blue-50 to-white px-8 md:px-36"
        >
          <div className="container mx-auto text-center bg-white shadow-2xl rounded-2xl p-12">
            <h2 className="text-4xl font-extrabold text-blue-800 mb-8">
              🌟 Business Process Solutions
            </h2>
            <p className="text-base text-gray-700 mb-6">
              Streamline operations and enhance customer satisfaction with our
              reliable BPO services:
            </p>
            <div className="grid gap-4 md:grid-cols-3 text-sm text-gray-700 text-left">
              <div className="bg-gray-100 p-4 rounded-xl">
                📞 Inbound Call Management
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                📣 Outbound Sales and Support
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                ✉️ Email and Chat Support Services
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                📲 SMS Campaign Management
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🗂️ Data Entry and Processing
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🤝 Customer Relationship Management (CRM)
              </div>
            </div>
          </div>
        </section>
      </motion.div>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section
          id="healthcare-solutions"
          className="py-24 bg-white px-8 md:px-36 mt-4"
        >
          <div className="container mx-auto text-center bg-blue-50 shadow-2xl rounded-2xl p-12">
            <h2 className="text-4xl font-extrabold text-blue-800 mb-8">
              🏥 Healthcare Solutions
            </h2>
            <p className="text-base text-gray-700 mb-6">
              Empowering healthcare providers with seamless support services:
            </p>
            <div className="grid gap-4 md:grid-cols-3 text-sm text-gray-700 text-left">
              <div className="bg-white p-4 rounded-xl border">
                💳 Medical Billing and Coding
              </div>
              <div className="bg-white p-4 rounded-xl border">
                📝 Medical Transcription Services
              </div>
              <div className="bg-white p-4 rounded-xl border">
                📄 Insurance Claims Processing
              </div>
              <div className="bg-white p-4 rounded-xl border">
                📊 Healthcare Data Management
              </div>
              <div className="bg-white p-4 rounded-xl border">
                📅 Patient Scheduling and Follow-up Services
              </div>
              <div className="bg-white p-4 rounded-xl border">
                💰 Revenue Cycle Management
              </div>
            </div>
          </div>
        </section>
      </motion.div>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section
          id="digital-transformation"
          className="mt-4 py-24 bg-gradient-to-r from-white to-blue-50 px-8 md:px-36"
        >
          <div className="container mx-auto text-center bg-white shadow-2xl rounded-2xl p-12">
            <h2 className="text-4xl font-extrabold text-blue-800 mb-8">
              💻 Digital Transformation Services
            </h2>
            <p className="text-base text-gray-700 mb-6">
              Helping your brand reach its audience and grow in the digital
              space:
            </p>
            <div className="grid gap-4 md:grid-cols-3 text-sm text-gray-700 text-left">
              <div className="bg-gray-100 p-4 rounded-xl">
                🌐 Custom Web Development
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🎨 UI/UX Design and Consultation
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🧩 WordPress Development
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🔍 Search Engine Optimization (SEO)
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                📱 Social Media Marketing and Management
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                💵 Pay-Per-Click (PPC) Advertising
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                ✍️ Content Writing and Copywriting
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🖼️ Graphic Design and Branding Solutions
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default OurExpertise;
